import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { useTranslation } from "@localization/useTranslation.ts";
import { usePlanContext } from "../../auth/planProvider/PlanProvider.tsx";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { usePermissionsProvider } from "../../auth/usePermissionsProvider.ts";
import useCountryHook from "@common/hooks/useCountryList.ts";
import { useSelectableRiskAreaLevelsQuery } from "@common/hooks/useSelectableItemQueries";
import { hydrateFilterOperators } from "@common/components/filterBar/filterOperatorHydration.ts";
import {
    getSelectableBusinessAreasUrl,
    selectableCertificationsUrl,
    selectableDataCategoriesUrl,
    selectableDataSubjectsUrl,
    selectableTagsUrl,
    useLegalEntityCustomInputFields,
    useSelectableAuditQuestionnairesQuery,
    useSelectableVendorClassificationsQuery,
} from "@common/hooks/useSelectableItemQueries.ts";
import { buildCustomFieldFilterOption } from "@common/components/filterPopover/customFieldFilterOption.ts";
import { getResponsibleQueryUrl } from "../../user/hooks/useResponsibleQuery.ts";
import { setFirstLetterToLowerCase } from "@common/stringOperations.ts";
import { TextFilter } from "@common/api/filterTypes.ts";
import { LegalEntityFilterRequest, LegalEntityScope } from "./LegalEntities.types.ts";
import { LEGAL_ENTITY_FILTER_ID_TO_BACKEND_PROPERTY } from "./LegalEntityFilter.types.ts";
import { legalEntityFilterGrouping } from "./legalEntityFieldGroupings.ts";
import { LevelOfProtectionType } from "../addLegalEntityDialog/AddLegalEntityDialog.types.ts";
import { useLegalEntityTypeNames } from "../useLegalEntityTypeNames.ts";
import { RiskLevel } from "../legalEntityClassificationTab/LegalEntityClassificationTab.types";
import { LegalEntityAuditStatus } from "../legalEntityAuditTab/LegalEntityAudit.types";
import { ControllerProcess } from "../../processingActivity/processingActivityOverview/ProcessingActivityOverview.types";
import { DocumentTypeUsageType } from "../../masterData/documentType/documentTypes/DocumentTypes.types";
import { LegalEntityAuditFrequency, LegalEntityAuditType } from "../LegalEntity.types";

// Every option the legacy page built from the fully loaded row set now comes from an endpoint or a static enum,
// because the overview is server paged and the current page is not the whole vocabulary.
export function useLegalEntityFilter(scope: LegalEntityScope, filterData?: Partial<LegalEntityFilterRequest>) {
    const { legalEntityTypes } = useLegalEntityTypeNames();
    const { translateString, translateLegalEntityAuditStatus, translatePropertyName } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures, vendorManagementFeatures, informationSecurityFeatures } = usePlanContext();
    const { hasEnterpriseRiskManagementAccess } = usePermissionsProvider();
    const { selectableCountries } = useCountryHook();

    const isGroupEntities = scope === LegalEntityScope.GroupEntity;
    const hasVendorManagement = vendorManagementFeatures.hasVendorManagement;
    // Same gate as the Certifications column in LegalEntityDisplayFields.hooks.ts.
    const hasCertifications = vendorManagementFeatures.certifications || informationSecurityFeatures.certifications;
    const hasTagsAccess = permissions.canAccessTags && platformFeatures.tags;

    // Fetched as fixedData rather than dataUrl because /filter-data resolves chip labels by EntityType, and
    // there is no entity type for a questionnaire - the chips would render blank.
    const customInputFieldsQuery = useLegalEntityCustomInputFields();
    const classificationsQuery = useSelectableVendorClassificationsQuery(hasVendorManagement);
    const latestAuditsQuery = useSelectableAuditQuestionnairesQuery(hasVendorManagement && !isGroupEntities);

    const riskAreaLevelsQuery = useSelectableRiskAreaLevelsQuery(hasEnterpriseRiskManagementAccess);

    function extractFilterValue(filter: any): any {
        if (!filter) {
            return "";
        }

        if (filter.value !== undefined && filter.filterOperator !== undefined) {
            return Array.isArray(filter.value) ? filter.value.map((item: string | number) => item.toString()) : filter.value;
        }

        return filter;
    }

    function extractBooleanFilterValue(value: boolean | undefined): Array<string> | string {
        return value === undefined ? "" : [value.toString()];
    }

    // Order mirrors the legacy Lists page so the two read the same while both are reachable.
    const legalEntityFilterOptions: Array<FilterOption> = [
        {
            id: "show-only-mine",
            label: translateString("show"),
            type: FilterOptionType.Boolean,
            value: filterData?.showOnlyMine ?? "",
            fixedData: [
                { id: "0", name: translateString("all") },
                { id: "1", name: translateString("my") },
            ],
        },
        {
            id: "legal-entity-status",
            label: translateString("status"),
            type: FilterOptionType.SelectField,
            singleSelect: true,
            value: extractBooleanFilterValue(filterData?.isActive),
            fixedData: [
                { id: "true", name: translateString("active") },
                { id: "false", name: translateString("archived") },
            ],
        },
        {
            id: "country-codes",
            label: translateString("countriesPlural"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.countryCodes) : "",
            fixedData: selectableCountries,
        },
        {
            id: "business-area-ids",
            label: translateString("businessAreas"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.businessAreaIds) : "",
            dataUrl: getSelectableBusinessAreasUrl(false),
        },
    ];

    if (hasVendorManagement) {
        legalEntityFilterOptions.push(
            {
                id: "classification-questionnaire-ids",
                label: translateString("classifications"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.classificationQuestionnaireIds) : "",
                fixedData: classificationsQuery.data ?? [],
            },
            {
                id: "classification-risk-levels",
                label: translateString("classificationRiskLevel"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.classificationRiskLevels) : "",
                fixedData: getRiskLevelOptions(),
            },
            {
                id: "areas",
                label: translateString("complianceAreas"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.complianceAreaIds) : "",
                dataUrl: "/areas/selectableitems",
            }
        );
    }

    if (hasEnterpriseRiskManagementAccess) {
        legalEntityFilterOptions.push({
            id: "risk-area-levels",
            label: translateString("risk"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.riskAreaLevels) : "",
            fixedData: riskAreaLevelsQuery.data ?? [],
        });
    }

    legalEntityFilterOptions.push({
        id: "responsible-ids",
        label: translateString("responsibles"),
        type: FilterOptionType.SelectField,
        value: filterData ? extractFilterValue(filterData.responsibleIds) : "",
        dataUrl: getResponsibleQueryUrl(),
    });

    if (hasVendorManagement && !isGroupEntities) {
        legalEntityFilterOptions.push(
            {
                id: "latest-audit-questionnaire-ids",
                label: translateString("legalEntitiesLatestAuditHeader"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.latestAuditQuestionnaireIds) : "",
                fixedData: latestAuditsQuery.data ?? [],
            },
            {
                id: "latest-audit-statuses",
                label: translateString("latestAuditStatus"),
                type: FilterOptionType.SelectField,
                value: filterData ? extractFilterValue(filterData.latestAuditStatuses) : "",
                fixedData: getAuditStatusOptions(),
            }
        );
    }

    if (!isGroupEntities) {
        legalEntityFilterOptions.push({
            id: "legal-entity-types",
            label: translateString("types"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.types) : "",
            fixedData: getLegalEntityTypeOptions(),
        });
    }

    if (isGroupEntities) {
        legalEntityFilterOptions.push({
            id: "preselected-on-create",
            label: translateString("preselectedOnCreate"),
            type: FilterOptionType.SelectField,
            singleSelect: true,
            value: extractBooleanFilterValue(filterData?.isPreselectedOnCreate),
            fixedData: [
                { id: "true", name: translateString("yes") },
                { id: "false", name: translateString("no") },
            ],
        });
    }

    if (hasTagsAccess) {
        legalEntityFilterOptions.push({
            id: "tag-ids",
            label: translateString("tags"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.tagIds) : "",
            dataUrl: selectableTagsUrl,
        });
    }

    // The rest of what customers' own company lists filter on. Labelled with the same propertyNames keys as the
    // column headers so a filter and the column it narrows read identically. The entity picker lists the
    // entities of the scope you are on, the same endpoints the system and contract filters use.
    legalEntityFilterOptions.push(
        {
            id: "legal-entity-ids",
            label: translateString(isGroupEntities ? "groupEntities" : "legalEntities"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.legalEntityIds) : "",
            dataUrl: isGroupEntities ? "/LegalEntity/selectableGroupEntities" : "/LegalEntity/selectable-external-entities",
        },
        {
            id: "locations",
            label: translatePropertyName("Location"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.locations) : "",
            fixedData: getLocationOptions(),
        },
        booleanFilterOption("tia-not-necessary", translatePropertyName("TIANotNecessary"), filterData?.isTransferImpactAssessmentUnnecessary),
        booleanFilterOption("transfer-basis-not-necessary", translateString("transferBasisNotRequired"), filterData?.isTransferBasisUnnecessary),
        {
            id: "transfer-basis-ids",
            label: translateString("transferBasis"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.transferBasisIds) : "",
            dataUrl: "/legalbasis/getTransferBasis",
        },
        {
            id: "sub-contractor-ids",
            label: translateString("subContractors"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.subContractorIds) : "",
            dataUrl: "/LegalEntity/selectableEntities",
        },
        booleanFilterOption("sender-on-source", translatePropertyName("IsCompanySenderOnSource"), filterData?.isSenderOnSource),
        booleanFilterOption("sender-on-data-processor", translatePropertyName("IsCompanySenderOnDataProcessor"), filterData?.isSenderOnDataProcessor),
        booleanFilterOption("data-processor", translatePropertyName("IsDataProcessor"), filterData?.isDataProcessor),
        booleanFilterOption("sub-processor", translatePropertyName("IsSubProcessor"), filterData?.isSubProcessor),
        booleanFilterOption("has-documents", translatePropertyName("CompanyHasDocuments"), filterData?.hasDocuments),
        {
            id: "document-type-ids",
            label: translatePropertyName("DocumentTypes"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.documentTypeIds) : "",
            dataUrl: `/documentTypes/selectableItems?usageType=${DocumentTypeUsageType.AllTransfers}`,
        },
        {
            id: "document-owner-ids",
            label: translatePropertyName("DocumentOwners"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.documentOwnerIds) : "",
            dataUrl: getResponsibleQueryUrl(),
        },
        {
            id: "document-group-entity-ids",
            label: translatePropertyName("GroupEntitiesOnDocuments"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.documentGroupEntityIds) : "",
            dataUrl: "/LegalEntity/selectableGroupEntities",
        },
        booleanFilterOption("has-sharings", translateString("hasSharingsYesNo"), filterData?.hasSharings),
        {
            id: "roles",
            label: translatePropertyName("Roles"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.roles) : "",
            fixedData: getRoleOptions(),
        },
        booleanFilterOption("has-processing-activities", translateString("hasProcessingActivitiesYesNo"), filterData?.hasProcessingActivities),
        {
            id: "data-categories",
            label: translateString("dataCategories"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.dataCategoryIds) : "",
            dataUrl: selectableDataCategoriesUrl,
        },
        booleanFilterOption("has-data-categories", translateString("hasDataCategoriesYesNo"), filterData?.hasDataCategories),
        {
            id: "data-subjects",
            label: translateString("dataSubjects"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.dataSubjectIds) : "",
            dataUrl: selectableDataSubjectsUrl,
        },
        booleanFilterOption("has-data-subjects", translateString("hasDataSubjectsYesNo"), filterData?.hasDataSubjects),
        booleanFilterOption("recipient-on-disclosure", translatePropertyName("IsCompanyRecipientOnDislosure"), filterData?.isRecipientOnDisclosure),
        booleanFilterOption("approved", translateString("approved"), filterData?.isApproved),
        {
            id: "supervision-types",
            label: translatePropertyName("LegalEntitySupervisionType"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.supervisionTypes) : "",
            fixedData: getSupervisionTypeOptions(),
        },
        {
            id: "supervision-frequencies",
            label: translatePropertyName("LegalEntitySupervisionFrequency"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.supervisionFrequencies) : "",
            fixedData: getSupervisionFrequencyOptions(),
        }
    );

    if (hasVendorManagement && hasCertifications) {
        legalEntityFilterOptions.push({
            id: "certifications",
            label: translateString("certifications"),
            type: FilterOptionType.SelectField,
            value: filterData ? extractFilterValue(filterData.certificationIds) : "",
            dataUrl: selectableCertificationsUrl,
        });
    }

    // The same lower-cased member keys the two columns translate their cells with.
    function getSupervisionTypeOptions() {
        return enumOptions(LegalEntityAuditType, [
            LegalEntityAuditType.AuditNotNecessary,
            LegalEntityAuditType.WrittenConfirmation,
            LegalEntityAuditType.Declaration,
            LegalEntityAuditType.AuditStatement,
            LegalEntityAuditType.SelfAssessment,
            LegalEntityAuditType.AuditOrAssessment,
            LegalEntityAuditType.Other,
        ]);
    }

    function getSupervisionFrequencyOptions() {
        return enumOptions(LegalEntityAuditFrequency, [
            LegalEntityAuditFrequency.Quarterly,
            LegalEntityAuditFrequency.SemiAnnually,
            LegalEntityAuditFrequency.Annual,
            LegalEntityAuditFrequency.EveryTwoYears,
            LegalEntityAuditFrequency.EveryThreeYears,
        ]);
    }

    function enumOptions<T extends number>(enumType: Record<number, string>, members: Array<T>) {
        return members.map((member) => ({ id: member.toString(), name: translateString(setFirstLetterToLowerCase(enumType[member])) }));
    }

    function booleanFilterOption(id: string, label: string, value: boolean | undefined): FilterOption {
        return {
            id,
            label,
            type: FilterOptionType.Boolean,
            value: value ?? "",
        };
    }

    // The four groups the Location column shows, with the same names. Mirrors
    // LegalEntitySqlQueryBuilder.GetProtectionLevelNames.
    function getLocationOptions() {
        return [
            { id: LevelOfProtectionType.EU.toString(), name: translateString("eu") },
            { id: LevelOfProtectionType.EEA.toString(), name: translateString("eea") },
            { id: LevelOfProtectionType.AdequateProtection.toString(), name: translateString("secureThirdCountries") },
            { id: LevelOfProtectionType.ThirdCountry.toString(), name: translateString("unsecureThirdCountries") },
        ];
    }

    // The four roles a sharing side can have; the Roles column renders the same keys.
    function getRoleOptions() {
        return [
            { id: ControllerProcess.Controller.toString(), name: translateString("Controller") },
            { id: ControllerProcess.ProcessorW.toString(), name: translateString("ProcessorW") },
            { id: ControllerProcess.Subprocessor.toString(), name: translateString("Subprocessor") },
            { id: ControllerProcess.JointControllers.toString(), name: translateString("JointControllers") },
        ];
    }

    // The custom list's per-column text filters, plus note and website which no list ever had.
    legalEntityFilterOptions.push(
        textFilterOption("description", translatePropertyName("Description"), filterData?.description),
        textFilterOption("address", translatePropertyName("Address"), filterData?.address),
        textFilterOption("zip-code", translatePropertyName("ZipCode"), filterData?.zipCode),
        textFilterOption("city", translatePropertyName("City"), filterData?.city),
        textFilterOption("company-number", translatePropertyName("CompanyNumber"), filterData?.companyNumber),
        textFilterOption("reference-number", translatePropertyName("ReferenceNumber"), filterData?.referenceNumber),
        textFilterOption("contacts", translatePropertyName("Contacts"), filterData?.contacts),
        textFilterOption("note", translateString("note"), filterData?.note),
        textFilterOption("website", translateString("website"), filterData?.website)
    );

    function textFilterOption(id: string, label: string, filter: TextFilter | undefined): FilterOption {
        return {
            id,
            label,
            type: FilterOptionType.TextField,
            value: filter ? extractFilterValue(filter) : "",
        };
    }

    // "Not answered" is deliberately absent: the backend predicate is an IN over lec.RiskLevel, which never
    // matches a row that has no classification answer, so the option could only ever return nothing.
    function getRiskLevelOptions() {
        return [RiskLevel.VeryLow, RiskLevel.Low, RiskLevel.Medium, RiskLevel.High, RiskLevel.VeryHigh].map((level) => ({
            id: level.toString(),
            name: translateString(setFirstLetterToLowerCase(RiskLevel[level])),
        }));
    }

    function getAuditStatusOptions() {
        return [
            LegalEntityAuditStatus.Sent,
            LegalEntityAuditStatus.Submitted,
            LegalEntityAuditStatus.DoesNotNeedToRespond,
            LegalEntityAuditStatus.DoesNotWantToRespond,
            LegalEntityAuditStatus.InProgress,
            LegalEntityAuditStatus.AuditOpinion,
            LegalEntityAuditStatus.Opened,
            LegalEntityAuditStatus.Other,
        ]
            .map((status) => ({ id: status.toString(), name: translateLegalEntityAuditStatus(status.toString()) }))
            .sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    // Every type the customer has, standard and their own, so the filter offers what the entities can carry.
    function getLegalEntityTypeOptions() {
        return legalEntityTypes.map((type) => ({ id: type.id, name: type.name })).sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    // The template's own fields, in their own group after the built-in ones. Each carries the input type so the
    // submenu and the chip know which editor to draw, exactly as the system and contract bars do.
    (customInputFieldsQuery.data ?? [])
        .slice()
        .sort((a, b) => (a.name > b.name ? 1 : -1))
        .forEach((customField) => {
            legalEntityFilterOptions.push(buildCustomFieldFilterOption(customField, filterData?.customFieldFilters, selectableCountries));
        });

    // Assigned in one pass from the grouping table rather than on every entry, so the menu's layout is described
    // in one place and the column picker cannot drift away from it.
    legalEntityFilterOptions.forEach((option) => {
        option.grouping = legalEntityFilterGrouping(option.id);
    });

    hydrateFilterOperators(legalEntityFilterOptions, filterData, LEGAL_ENTITY_FILTER_ID_TO_BACKEND_PROPERTY);

    return {
        legalEntityFilterOptions,
        isLoading: classificationsQuery.isLoading || latestAuditsQuery.isLoading || riskAreaLevelsQuery.isLoading,
    };
}
